import React, { useEffect, useRef, useState } from "react";
import { MapPin, Search, X } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Searchable city picker backed by /api/reference/cities/search (LGD data).
 * value: { city_id, city_name, state_name } | null
 * onChange(cityOrNull) — city = { id, name, display_name, state_name, lgd_code }
 */
export const CityPicker = ({ value, onChange, placeholder = "Search city…", testid = "city-picker", showServiceability = false }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);
  const timer = useRef(null);

  useEffect(() => {
    const close = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const search = (text) => {
    setQ(text);
    clearTimeout(timer.current);
    if (text.trim().length < 2) { setResults([]); setOpen(false); return; }
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await api.get(`/reference/cities/search?q=${encodeURIComponent(text)}&limit=10${showServiceability ? "&include_serviceability=1" : ""}`);
        setResults(r.data || []);
        setOpen(true);
      } catch (_e) { setResults([]); }
      setLoading(false);
    }, 250);
  };

  if (value?.city_id) {
    return (
      <div className="mt-1 flex items-center justify-between h-10 px-3 rounded-md border border-slate-200 bg-slate-50"
           data-testid={`${testid}-selected`}>
        <span className="flex items-center gap-2 text-sm text-slate-700">
          <MapPin size={13} className="text-slate-400" />
          {value.city_name}{value.state_name ? `, ${value.state_name}` : ""}
        </span>
        <button type="button" onClick={() => onChange(null)} data-testid={`${testid}-clear`}
                className="text-slate-400 hover:text-slate-600"><X size={14} /></button>
      </div>
    );
  }

  return (
    <div className="relative mt-1" ref={boxRef}>
      <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
      <input
        value={q}
        onChange={(e) => search(e.target.value)}
        onFocus={() => results.length && setOpen(true)}
        placeholder={placeholder}
        data-testid={`${testid}-input`}
        className="w-full h-10 pl-8 pr-3 rounded-md border border-slate-200 text-sm outline-none focus:border-slate-400 bg-white"
      />
      {open && (
        <div className="absolute z-30 mt-1 w-full max-h-64 overflow-auto rounded-xl border border-slate-200 bg-white shadow-lg"
             data-testid={`${testid}-results`}>
          {loading && <div className="px-3 py-2 text-xs text-slate-400">Searching…</div>}
          {!loading && results.length === 0 && (
            <div className="px-3 py-2 text-xs text-slate-400" data-testid={`${testid}-no-results`}>No matching city found</div>
          )}
          {results.map((c) => (
            <button key={c.id} type="button" data-testid={`${testid}-option-${c.id}`}
                    onClick={() => { onChange(c); setQ(""); setResults([]); setOpen(false); }}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center gap-2">
              <MapPin size={12} className="text-slate-400 shrink-0" />
              <span className="flex-1">{c.display_name || `${c.name}, ${c.state_name}`}</span>
              {showServiceability && c.is_serviceable === false && (
                <span className="text-[10px] text-amber-700">Unavailable</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
